import React from 'react';
import './Pages.css';

const Support: React.FC = () => {
  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Support Center</h1>
        <p className="page-subtitle">We're here to help you get the most out of GET AI PORTRAIT</p>
      </div>

      <div className="page-content legal-content">
        <section className="content-section">
          <h2>📧 Contact Us</h2>
          <p>
            We're here to help! For any questions, issues, or feedback, please reach out to us:
          </p>
          <div className="contact-box">
            <p>
              <strong>General Support:</strong><br />
              📧 <a href="mailto:support@dreif-ai.com">support@dreif-ai.com</a><br />
              Response Time: Within 24 hours
            </p>
            <p>
              <strong>Privacy & Data Protection:</strong><br />
              📧 <a href="mailto:privacy@dreif-ai.com">privacy@dreif-ai.com</a>
            </p>
            <p>
              <strong>Press & Media:</strong><br />
              📧 <a href="mailto:press@dreif-ai.com">press@dreif-ai.com</a>
            </p>
            <p>
              <strong>Legal Inquiries:</strong><br />
              📧 <a href="mailto:legal@dreif-ai.com">legal@dreif-ai.com</a>
            </p>
            <p>
              <strong>Support Hours:</strong><br />
              Monday-Friday, 9 AM - 6 PM CET<br />
              We typically respond within 24 hours
            </p>
          </div>
        </section>

        <section className="content-section">
          <h2>❓ Frequently Asked Questions</h2>
          <p>
            Before contacting support, check if your question is answered in our comprehensive{' '}
            <a href="/faq">FAQ page</a>. We cover topics including:
          </p>
          <ul>
            <li>How GET AI PORTRAIT works</li>
            <li>Pricing and subscriptions</li>
            <li>Privacy and data security (GDPR compliance)</li>
            <li>AI technology and transparency</li>
            <li>Portrait quality and processing times</li>
            <li>Commercial usage rights</li>
            <li>Device compatibility</li>
            <li>And much more!</li>
          </ul>
          <a href="/faq" style={{ display: 'inline-block', backgroundColor: '#0AD4CF', color: 'white', padding: '10px 20px', borderRadius: '8px', textDecoration: 'none', marginTop: '10px' }}>
            📚 Visit FAQ Page
          </a>
        </section>

        <section className="content-section">
          <h2>🔧 Common Issues & Quick Fixes</h2>

          <h3>How do I restore my purchases?</h3>
          <p>
            Open the app → Settings → Restore Purchases. Your premium access will be restored automatically
            if you've purchased a subscription or portrait pack through the same Apple ID.
          </p>

          <h3>Why is my portrait generation taking so long?</h3>
          <p>
            AI portrait generation typically takes under 60 seconds. During peak times, it may take up to
            2 minutes. If it takes longer:
          </p>
          <ul>
            <li>Check your internet connection</li>
            <li>Ensure you have a stable Wi-Fi or cellular connection</li>
            <li>Try closing and reopening the app</li>
            <li>Check the Pending Jobs screen for status updates</li>
          </ul>

          <h3>How do I cancel my subscription?</h3>
          <p>
            Subscriptions are managed through the App Store:
          </p>
          <ol>
            <li>Open iOS Settings</li>
            <li>Tap your name at the top</li>
            <li>Select "Subscriptions"</li>
            <li>Find GET AI PORTRAIT</li>
            <li>Tap "Cancel Subscription"</li>
          </ol>
          <p>
            You'll continue to have access until the end of your paid period. See our{' '}
            <a href="/refund-policy">Refund Policy</a> for details.
          </p>

          <h3>Can I use the portraits commercially?</h3>
          <p>
            Yes! Premium users have full ownership of generated portraits and may use them for:
          </p>
          <ul>
            <li>Social media profiles (Instagram, TikTok, LinkedIn)</li>
            <li>Dating apps (Tinder, Bumble, Hinge)</li>
            <li>Business cards and marketing materials</li>
            <li>Professional headshots</li>
            <li>Website and portfolio</li>
            <li>Any personal or commercial purpose</li>
          </ul>
          <p>
            Please review our <a href="/terms">Terms of Service</a> for complete details.
          </p>

          <h3>What photo formats are supported?</h3>
          <p>
            We support all standard iOS photo formats:
          </p>
          <ul>
            <li>JPEG (.jpg, .jpeg)</li>
            <li>PNG (.png)</li>
            <li>HEIC (Apple's native format)</li>
          </ul>
          <p>
            <strong>Tips for best results:</strong>
          </p>
          <ul>
            <li>Use well-lit, front-facing selfies</li>
            <li>Ensure face is clearly visible</li>
            <li>Upload 5-10 photos with variety</li>
            <li>Avoid blurry or low-resolution images</li>
          </ul>

          <h3>How do I delete my account and data?</h3>
          <p>
            You have full control over your data:
          </p>
          <ol>
            <li>Open the app</li>
            <li>Go to Settings</li>
            <li>Scroll to "Privacy & Data"</li>
            <li>Tap "Delete All Data"</li>
          </ol>
          <p>
            Or email us at <a href="mailto:privacy@dreif-ai.com">privacy@dreif-ai.com</a> with your request.
            See our <a href="/data-deletion">Data Deletion</a> page for detailed instructions.
          </p>
        </section>

        <section className="content-section">
          <h2>🛠️ Troubleshooting</h2>

          <h3>App Crashes or Freezes</h3>
          <ul>
            <li>Force quit the app (swipe up from bottom and swipe up on app)</li>
            <li>Restart the app</li>
            <li>Check for app updates in the App Store</li>
            <li>Ensure you have at least 500MB free storage</li>
            <li>Restart your device if issues persist</li>
            <li>Uninstall and reinstall the app (your data syncs via CloudKit)</li>
          </ul>

          <h3>Portrait Quality Issues</h3>
          <ul>
            <li>Use high-quality, well-lit photos (natural lighting preferred)</li>
            <li>Ensure face is clearly visible and front-facing</li>
            <li>Avoid blurry, pixelated, or low-resolution images</li>
            <li>Try uploading different photos with variety</li>
            <li>Check that faces are detected correctly (green checkmark)</li>
            <li>Experiment with different styles for your face type</li>
          </ul>

          <h3>Payment & Subscription Issues</h3>
          <ul>
            <li>Verify your payment method in App Store settings</li>
            <li>Check for any payment restrictions on your Apple ID</li>
            <li>Try restoring purchases in app settings</li>
            <li>Ensure you're signed in with the correct Apple ID</li>
            <li>Contact us if charges appear incorrect</li>
            <li>Request refunds through{' '}
              <a href="https://reportaproblem.apple.com" target="_blank" rel="noopener noreferrer">
                reportaproblem.apple.com
              </a>
            </li>
          </ul>

          <h3>CloudKit Sync Issues</h3>
          <ul>
            <li>Ensure you're signed in with iCloud (Settings → Apple ID)</li>
            <li>Check that iCloud Drive is enabled</li>
            <li>Verify stable internet connection</li>
            <li>Allow GET AI PORTRAIT to access iCloud in iOS Settings</li>
            <li>Sync may take a few minutes for large galleries</li>
          </ul>

          <h3>Face Detection Not Working</h3>
          <ul>
            <li>Ensure face is clearly visible and not obscured</li>
            <li>Use front-facing photos (not profile or side angles)</li>
            <li>Check lighting (avoid shadows or extreme backlighting)</li>
            <li>Remove sunglasses, masks, or obstructions</li>
            <li>Try a different photo if detection fails repeatedly</li>
          </ul>
        </section>

        <section className="content-section">
          <h2>💡 Feature Requests & Feedback</h2>
          <p>
            We love hearing from our users! Your feedback shapes the future of GET AI PORTRAIT.
          </p>
          <p>
            <strong>Have a suggestion?</strong>
          </p>
          <ul>
            <li>New portrait styles you'd like to see</li>
            <li>Feature improvements or additions</li>
            <li>UI/UX enhancements</li>
            <li>Performance optimizations</li>
            <li>Integration requests (social media, etc.)</li>
          </ul>
          <p>
            Send your ideas to{' '}
            <a href="mailto:support@dreif-ai.com?subject=Feature Request">support@dreif-ai.com</a> with
            "Feature Request" in the subject line. Popular requests are prioritized for development!
          </p>
        </section>

        <section className="content-section">
          <h2>🔒 Privacy & Data</h2>
          <p>
            For privacy-related inquiries, please see our <a href="/privacy">Privacy Policy</a> or contact
            our Data Protection Officer at <a href="mailto:privacy@dreif-ai.com">privacy@dreif-ai.com</a>.
          </p>
          <p>
            <strong>Key Privacy Points:</strong>
          </p>
          <ul>
            <li>Face detection for artistic transformation only (NOT facial recognition)</li>
            <li>Biometric data deleted immediately after portrait generation</li>
            <li>Photos deleted from servers within 24 hours</li>
            <li>GDPR & EU AI Act compliant</li>
            <li>No facial recognition profiles or databases</li>
            <li>You control all data through app settings</li>
          </ul>
          <p>
            <strong>Related Resources:</strong>
          </p>
          <ul>
            <li><a href="/privacy">Privacy Policy</a> - How we protect your data</li>
            <li><a href="/gdpr">GDPR Rights</a> - Your data protection rights</li>
            <li><a href="/data-deletion">Data Deletion</a> - How to delete your data</li>
            <li><a href="/ai-disclosure">AI Disclosure</a> - How our AI technology works</li>
          </ul>
        </section>

        <section className="content-section">
          <h2>📚 Additional Resources</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px' }}>
            <div style={{ padding: '15px', backgroundColor: '#f0f9ff', borderRadius: '8px' }}>
              <h3 style={{ marginTop: 0 }}>📄 Legal</h3>
              <ul style={{ listStyle: 'none', padding: 0 }}>
                <li><a href="/terms">Terms of Service</a></li>
                <li><a href="/privacy">Privacy Policy</a></li>
                <li><a href="/eula">EULA</a></li>
                <li><a href="/ai-disclosure">AI Disclosure</a></li>
              </ul>
            </div>
            <div style={{ padding: '15px', backgroundColor: '#f0f9ff', borderRadius: '8px' }}>
              <h3 style={{ marginTop: 0 }}>🔐 Privacy</h3>
              <ul style={{ listStyle: 'none', padding: 0 }}>
                <li><a href="/gdpr">GDPR Rights</a></li>
                <li><a href="/data-deletion">Data Deletion</a></li>
                <li><a href="/cookie-policy">Cookie Policy</a></li>
              </ul>
            </div>
            <div style={{ padding: '15px', backgroundColor: '#f0f9ff', borderRadius: '8px' }}>
              <h3 style={{ marginTop: 0 }}>💰 Billing</h3>
              <ul style={{ listStyle: 'none', padding: 0 }}>
                <li><a href="/refund-policy">Refund Policy</a></li>
                <li><a href="https://reportaproblem.apple.com" target="_blank" rel="noopener noreferrer">Request Refund</a></li>
              </ul>
            </div>
            <div style={{ padding: '15px', backgroundColor: '#f0f9ff', borderRadius: '8px' }}>
              <h3 style={{ marginTop: 0 }}>📰 Company</h3>
              <ul style={{ listStyle: 'none', padding: 0 }}>
                <li><a href="/about">About Us</a></li>
                <li><a href="/press">Press Kit</a></li>
              </ul>
            </div>
          </div>
        </section>

        <section className="content-section">
          <h2>🏢 Legal Information</h2>
          <div className="contact-box">
            <p>
              <strong>Company:</strong> DREIF AI<br />
              <strong>Operator:</strong> Dominik Reif<br />
              <strong>Address:</strong><br />
              Waldstraße 14<br />
              91091 Großenseebach<br />
              Germany
            </p>
            <p>
              <strong>General Inquiries:</strong> <a href="mailto:support@dreif-ai.com">support@dreif-ai.com</a><br />
              <strong>Privacy:</strong> <a href="mailto:privacy@dreif-ai.com">privacy@dreif-ai.com</a><br />
              <strong>Press:</strong> <a href="mailto:press@dreif-ai.com">press@dreif-ai.com</a><br />
              <strong>Legal:</strong> <a href="mailto:legal@dreif-ai.com">legal@dreif-ai.com</a>
            </p>
            <p>
              <strong>Website:</strong>{' '}
              <a href="https://getaiportrait.com" target="_blank" rel="noopener noreferrer">
                getaiportrait.com
              </a><br />
              <strong>Company Site:</strong>{' '}
              <a href="https://dreif-ai.com" target="_blank" rel="noopener noreferrer">
                dreif-ai.com
              </a>
            </p>
          </div>
        </section>

        <div style={{ marginTop: '40px', padding: '20px', backgroundColor: '#f0f9ff', borderRadius: '8px', textAlign: 'center' }}>
          <h3 style={{ marginTop: 0, color: '#0AD4CF' }}>Still need help?</h3>
          <p style={{ marginBottom: '15px' }}>
            Our support team is ready to assist you with any questions or issues.
          </p>
          <a
            href="mailto:support@dreif-ai.com"
            style={{
              display: 'inline-block',
              backgroundColor: '#0AD4CF',
              color: 'white',
              padding: '12px 30px',
              borderRadius: '8px',
              textDecoration: 'none',
              fontSize: '1.1rem',
              fontWeight: 600
            }}
          >
            📧 Email Support
          </a>
        </div>
      </div>
    </div>
  );
};

export default Support;
